import * as React from "react"

import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"

import { colorSerie, colorSerieSuave } from "@/components/charts/palette"
import { t as traducir } from "@/lib/i18n"

export interface Serie {
  nombre: string
  /**
   * `null` es UN HUECO, no un cero.
   *
   * La distinción es la que salva al gráfico de mentir. Un mes que no se pudo
   * consolidar —faltaba una cotización— dibujado como cero se lee como un mes
   * sin actividad, y una serie con dos ceros así parece una caída del negocio.
   * Un hueco se deja sin punto y con la línea partida: se ve que falta, que es
   * lo que de verdad pasó.
   */
  valores: Array<number | null>
}

interface Props {
  /** Etiqueta de cada punto del eje X: "Ene", "Q1", "2026"… */
  etiquetas: string[]
  series: Serie[]
  /** `barras` compara categorias lado a lado; `linea`, evolucion en el tiempo. */
  tipo?: "linea" | "barras"
  alto?: number
  /** Cero explicito: en dinero el cero es una referencia, no un minimo. */
  formato?: (v: number) => string
  /**
   * EL CORTE ENTRE LO REAL Y LO PROYECTADO: índice del último punto que es un
   * hecho. Dibuja la línea roja justo a su derecha.
   *
   * Cesar, punto 5 de la cuarta tanda: «en finanzas, forecast, falta la línea
   * roja que separa real del forecast mismo». Las tablas la tenían
   * (`.corte-real` en `index.css`) y el gráfico no, que es justo donde más
   * falta hace: una curva que sube no dice en qué punto dejó de ser un hecho, y
   * tres escenarios que se separan a mitad del dibujo se leen como si la
   * separación fuera del negocio y no del corte.
   *
   * `null` = no hay corte declarado, y entonces NO se dibuja nada: una línea en
   * el mes cero diría que nada es real, que es distinto de «nadie lo ha dicho».
   */
  corte?: number | null
  className?: string
}

/*
  EL MARGEN DEL AREA DE DIBUJO. El izquierdo no esta aqui: se mide, ver
  `margenIzquierdo`.

  ARRIBA ERAN 8 PX Y ERAN POCOS. Cesar: «los cuadros de lineas y de barras
  mejoraron pero mejora el espaciado porque estan muy pegados hacia arriba,
  deberia de verse mas limpio y ordenado».

  Con 8 px la marca mas alta del eje quedaba a media altura de su propio texto
  del borde del SVG —el texto se centra en la marca, asi que con 8 px de margen
  se sale casi 4— y el punto mas alto de la serie tocaba el filo. Tocar el filo
  hace que el grafico parezca RECORTADO y no lleno: no se sabe si la serie sigue
  subiendo por encima. Con 18 px cabe el texto de la marca entera y queda aire
  sobre el maximo, que es lo que se lee como «esto es todo lo que hay».

  El de abajo se queda en 22: ahi van las etiquetas del eje horizontal y estan
  medidas.
*/
const M = { top: 18, right: 8, bottom: 22 }

/**
 * Cuanto sitio hay que reservar a la izquierda para que quepa la escala.
 *
 * EL MARGEN FIJO DE 56 PX RECORTABA LAS CIFRAS, y recortar una cifra por la
 * IZQUIERDA no la deja ilegible: la deja MINTIENDO. «538.645,00» dibujado en un
 * hueco de 56 px se ve como «38.645,00», que es un numero plausible, esta bien
 * formateado y es diez veces menor. Nadie lo cuestiona porque no parece roto.
 *
 * Asi que se mide. Onest a 9 px con cifras tabulares da un avance de ~5,15 px
 * por digito; los separadores son mas estrechos, pero redondear hacia arriba
 * cuesta unos pixeles de ancho y equivocarse cuesta un dato falso. Se toma la
 * marca mas larga de las que se van a pintar —no la mayor en valor: «-9.871,00»
 * es mas ancha que «10.000,00»—, y hay un suelo para que un grafico de valores
 * de una cifra no pegue el eje al borde.
 */
export function margenIzquierdo(etiquetas: string[]): number {
  const anchoMax = Math.max(0, ...etiquetas.map((e) => e.length)) * 5.15
  return Math.min(120, Math.max(40, Math.ceil(anchoMax) + 10))
}

/**
 * Grafico de series, en SVG y sin dependencias.
 *
 * DECISIONES QUE SIGUEN LA GUIA DE VISUALIZACION:
 *
 * - UN SOLO EJE. Nunca dos escalas verticales. Si hay que comparar ingresos con
 *   un porcentaje, van dos graficos, no dos ejes: un eje doble deja que
 *   cualquiera "demuestre" la correlacion que quiera moviendo una escala.
 * - COLOR POR ENTIDAD, en orden fijo. La serie 1 es siempre `--chart-1`.
 * - LEYENDA SIEMPRE con dos o mas series; con una sola, el titulo ya la nombra.
 *   La identidad nunca depende solo del color.
 * - MARCAS FINAS, rejilla discreta: la linea de datos tiene que ser lo mas
 *   marcado del dibujo.
 * - EL CERO SIEMPRE ESTA EN LA ESCALA cuando hay negativos, y se dibuja. Un
 *   grafico de caja sin la linea de cero esconde justo lo que se viene a ver.
 * - CAPA DE HOVER incluida: un grafico en HTML es interactivo; sin tooltip el
 *   lector tiene que estimar cada valor contra el eje.
 *
 * ---------------------------------------------------------------------------
 * Y LO QUE FIJA LA LAMINA 19 DEL BRANDBOOK, que faltaba entero
 *
 * «Graficos de linea FINA sobre el RAYADO; **el ultimo dato siempre en rojo**.»
 * Tres cosas, y ninguna es decorativa:
 *
 * 1. **El rayado detras del area de datos.** Es la metafora del producto: el
 *    grafico se dibuja sobre el papel rayado del libro mayor. Va al paso de 32 px
 *    que fija `--rayado-paso`, el mismo del riel y de las notas de Marty, asi que
 *    no es una trama nueva: es LA trama. Sustituye a la rejilla horizontal
 *    propia, que hacia lo mismo peor — dos juegos de lineas horizontales
 *    compitiendo. Las marcas del eje se quedan, pero solo como NUMERO.
 *
 * 2. **Linea fina.** 1,5 px en vez de 2. Con el rayado detras, un trazo de 2 px
 *    pesa mas que el papel y el dibujo deja de parecer un libro.
 *
 * 3. **EL ULTIMO DATO EN ROJO**, con `--chart-acento`, que esta fuera de la
 *    paleta categorica precisamente para esto: no es una serie mas, es «hasta
 *    aqui llegamos». Solo en la PRIMERA serie y solo en lineas: el rojo senala y
 *    no tapiza, y cuatro ultimos puntos rojos —uno por serie— no senalan nada.
 *    En barras no se aplica: ahi el ultimo no tiene el significado de «lo mas
 *    reciente» que tiene en una trayectoria.
 */
export function Chart({
  etiquetas, series, tipo = "linea", alto = 200, formato = formatAmount,
  corte = null, className,
}: Props) {
  const [activo, setActivo] = React.useState<number | null>(null)
  const [ancho, setAncho] = React.useState(720)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(([e]) => setAncho(e.contentRect.width))
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  const n = etiquetas.length
  // Los huecos NO entran en la escala: un `null` tratado como cero arrastraría
  // el mínimo hasta el suelo y aplastaría la serie entera contra el techo.
  const planos = series.flatMap((s) => s.valores).filter((v): v is number => v != null)
  const max = Math.max(0, ...planos)
  const min = Math.min(0, ...planos)
  const rango = max - min || 1

  const W = Math.max(ancho, 320)
  const H = alto

  // Cuatro marcas de eje: mas lineas compiten con los datos. Desde la lamina
  // 19 son solo el NUMERO — las horizontales las pone el rayado.
  const marcas = [0, 0.25, 0.5, 0.75, 1].map((t) => min + t * rango)
  // El margen se MIDE sobre lo que se va a escribir. Ver `margenIzquierdo`.
  const izq = margenIzquierdo(marcas.map(formato))

  const px = (i: number) => izq + (n <= 1 ? 0 : (i * (W - izq - M.right)) / (n - 1))
  const py = (v: number) => M.top + ((max - v) / rango) * (H - M.top - M.bottom)
  const y0 = py(0)

  // Los renglones del rayado: al paso de las marcas del eje y llenando el area.
  const paso = (H - M.top - M.bottom) / 4
  const renglones: number[] = []
  for (let y = py(marcas[marcas.length - 1]); y <= H - M.bottom + 0.5; y += paso) {
    renglones.push(Math.round(y) + 0.5)  // el medio pixel evita el trazo borroso
  }

  const anchoBanda = n > 0 ? (W - izq - M.right) / n : 0
  const anchoBarra = tipo === "barras" && series.length
    ? Math.max(3, (anchoBanda * 0.62) / series.length)
    : 0

  return (
    /*
      EL RELLENO LO PONE EL GRAFICO. `WorkPanel` deja el cuerpo sin relleno a
      proposito —las tablas van a sangre, de filete a filete— y por eso el SVG
      arrancaba pegado al borde inferior de la cabecera. Un grafico si necesita
      aire siempre, en los cuatro lados, asi que se lo pone el mismo en vez de
      obligar a cada una de las cinco pantallas que lo montan a acordarse.
    */
    <div ref={ref} className={cn("w-full px-2.5 pb-1.5 pt-3", className)}>
      {series.length > 1 ? (
        <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {series.map((s, i) => (
            <span key={s.nombre} className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-[2px]"
                style={{ background: colorSerie(i) }}
              />
              {s.nombre}
            </span>
          ))}
        </div>
      ) : null}

      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${series.map((s) => s.nombre).join(" y ")} por período`}
        onMouseLeave={() => setActivo(null)}
      >
        {/*
          EL RAYADO, detras de los datos, y CAYENDO SOBRE LAS MARCAS DEL EJE.

          Un rayado a paso fijo —32 px, como el del riel— dejaria cada numero del
          eje flotando entre dos lineas, y entonces el rayado no seria la rejilla
          sino una trama encima de ella: dos juegos de horizontales compitiendo.
          Rayado y marca son LA MISMA LINEA, que es exactamente lo que es un
          libro mayor: los numeros se escriben SOBRE el renglon.

          Se dibujan tantos renglones como caben, al paso de las marcas, para que
          la textura llegue a los bordes del area y no se corte donde acaba la
          escala.
        */}
        {renglones.map((y, i) => (
          <line
            key={`r-${i}`}
            x1={izq} x2={W - M.right} y1={y} y2={y}
            stroke="hsl(var(--rayado))" strokeWidth={1}
          />
        ))}

        {marcas.map((v, i) => (
          <text
            key={i}
            x={izq - 6} y={py(v) + 3} textAnchor="end"
            className="fill-muted-foreground" style={{ fontSize: 9 }}
          >
            {formato(v)}
          </text>
        ))}

        {/* El cero, marcado aparte: separa ganar de perder. */}
        {min < 0 ? (
          <line x1={izq} x2={W - M.right} y1={y0} y2={y0}
                stroke="hsl(var(--foreground) / 0.35)" strokeWidth={1} />
        ) : null}

        {/*
          LA LÍNEA DEL CORTE: a la DERECHA del último punto real, no encima.

          Encima del punto se leería como «este mes es la frontera», y el mes del
          corte ES real — la frontera está entre él y el siguiente. Va a media
          banda, que es donde de verdad cae el límite.

          Discontinua y con `--chart-acento`, el mismo rojo que marca el último
          dato: no es una serie más, es «hasta aquí es un hecho». Se dibuja antes
          que las series para que no las tape.
        */}
        {corte != null && corte >= 0 && corte < n - 1 ? (
          <line
            x1={px(corte) + (px(1) - px(0)) / 2}
            x2={px(corte) + (px(1) - px(0)) / 2}
            y1={M.top} y2={H - M.bottom}
            stroke="hsl(var(--chart-acento))" strokeWidth={1.5} strokeDasharray="4 3"
          />
        ) : null}

        {tipo === "linea"
          ? series.map((s, si) => {
              /*
                LA LÍNEA SE PARTE EN LOS HUECOS. Un `L` que salte por encima de
                un `null` uniría los dos meses que lo rodean con un trazo recto,
                que es exactamente afirmar lo que no se sabe: que entre ambos
                pasó algo intermedio. Se arranca un subtrazo con `M` después de
                cada hueco.
              */
              let d = ""
              let cortado = true
              s.valores.forEach((v, i) => {
                if (v == null) { cortado = true; return }
                d += `${cortado ? "M" : "L"}${px(i)},${py(v)}`
                cortado = false
              })
              // EL ÚLTIMO DATO es el último que EXISTE, no la última posición:
              // si diciembre no se pudo consolidar, el acento va en noviembre.
              const ultimo = s.valores.reduce(
                (ult, v, i) => (v != null ? i : ult), -1)
              return (
                <g key={s.nombre}>
                  <path d={d} fill="none" stroke={colorSerie(si)} strokeWidth={1.5}
                        strokeLinejoin="round" strokeLinecap="round" />
                  {s.valores.map((v, i) => {
                    if (v == null) return null
                    // El ultimo dato en rojo, y solo en la primera serie.
                    const acento = si === 0 && i === ultimo
                    return (
                      <circle
                        key={i} cx={px(i)} cy={py(v)}
                        r={acento ? 3.5 : activo === i ? 4 : 2.5}
                        fill={acento ? "hsl(var(--chart-acento))" : colorSerie(si)}
                        stroke="hsl(var(--surface))" strokeWidth={1.5}
                      />
                    )
                  })}
                </g>
              )
            })
          : series.map((s, si) =>
              s.valores.map((v, i) => {
                if (v == null) return null
                const base = izq + i * anchoBanda + anchoBanda * 0.19
                const x = base + si * anchoBarra
                const y = v >= 0 ? py(v) : y0
                const h = Math.max(1, Math.abs(py(v) - y0))
                return (
                  <rect
                    key={`${s.nombre}-${i}`}
                    x={x + 1} y={y} width={Math.max(1, anchoBarra - 2)} height={h}
                    rx={2} fill={colorSerie(si)}
                    opacity={activo === null || activo === i ? 1 : 0.45}
                  />
                )
              }),
            )}

        {/*
          EL HUECO SE MARCA. Si sólo se omitiera el punto, «no hay dato» y «el
          valor es cero» se verían igual —ambos como un espacio en blanco— y
          estaríamos donde empezamos. La trama diagonal dice que ahí falta algo.
        */}
        {etiquetas.map((_, i) =>
          series.length && series.every((s) => s.valores[i] == null) ? (
            <rect
              key={`hueco-${i}`}
              className="hatch-alert"
              x={tipo === "barras" ? izq + i * anchoBanda : px(i) - anchoBanda / 2}
              y={M.top}
              width={Math.max(anchoBanda, 6)}
              height={Math.max(0, H - M.top - M.bottom)}
              fill="hsl(var(--muted-foreground) / 0.06)"
            />
          ) : null,
        )}

        {etiquetas.map((e, i) => (
          <text
            key={e} x={tipo === "barras" ? izq + i * anchoBanda + anchoBanda / 2 : px(i)}
            y={H - 6} textAnchor="middle"
            className={cn("fill-muted-foreground", activo === i && "fill-foreground")}
            style={{ fontSize: 9 }}
          >
            {e}
          </text>
        ))}

        {/* Zonas de hover anchas: el objetivo es mas grande que la marca. */}
        {etiquetas.map((_, i) => (
          <rect
            key={`hit-${i}`}
            x={tipo === "barras" ? izq + i * anchoBanda : px(i) - anchoBanda / 2}
            y={0} width={Math.max(anchoBanda, 8)} height={H}
            fill="transparent" onMouseEnter={() => setActivo(i)}
          />
        ))}

        {activo !== null && tipo === "linea" ? (
          <line x1={px(activo)} x2={px(activo)} y1={M.top} y2={H - M.bottom}
                stroke="hsl(var(--foreground) / 0.2)" strokeWidth={1} />
        ) : null}
      </svg>

      {activo !== null ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs">
          <span className="font-medium">{etiquetas[activo]}</span>
          {series.map((s, i) => (
            <span key={s.nombre} className="flex items-center gap-1 text-muted-foreground">
              <span aria-hidden="true" className="h-2 w-2 rounded-[2px]"
                    style={{ background: colorSerie(i) }} />
              {s.nombre}
              <span className="num font-medium text-foreground">
                {s.valores[activo] == null
                  ? /* «sin dato» y «0,00» no son lo mismo tampoco aquí. */ "sin dato"
                  : formato(s.valores[activo] as number)}
              </span>
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-1 h-4 text-2xs text-muted-foreground">
          {traducir("Pasa el cursor por un período para ver los valores.")}</div>
      )}
    </div>
  )
}

/** Barras horizontales: magnitud comparada entre pocas categorías con nombre. */
export function BarrasHorizontales({
  filas, formato = formatAmount,
}: {
  filas: Array<{ etiqueta: string; valor: number }>
  formato?: (v: number) => string
}) {
  const max = Math.max(1, ...filas.map((f) => Math.abs(f.valor)))
  const total = filas.reduce((s, f) => s + Math.max(0, f.valor), 0)
  return (
    <div className="space-y-2.5 px-2.5 py-2">
      {filas.map((f, i) => (
        <div key={f.etiqueta} className="flex items-center gap-3">
          <span className="w-20 shrink-0 truncate text-xs">
            {f.etiqueta}
          </span>
          <div className="h-5 flex-1 rounded-[3px] bg-surface-sunken">
            <div
              className="h-full rounded-[3px] transition-[width] duration-300 ease-out"
              style={{
                width: `${(Math.abs(f.valor) / max) * 100}%`,
                background: f.valor < 0 ? "hsl(var(--destructive))" : colorSerie(i),
              }}
            />
          </div>
          <span className="num w-28 shrink-0 text-xs">{formato(f.valor)}</span>
          <span className="num w-12 shrink-0 text-right text-2xs text-muted-foreground">
            {total > 0 && f.valor > 0 ? `${Math.round((f.valor / total) * 100)}%` : ""}
          </span>
        </div>
      ))}
    </div>
  )
}
